const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-v3'

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

function isRecaptchaReady(): boolean {
  return (
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.ready === 'function' &&
    typeof window.grecaptcha.execute === 'function'
  )
}

function waitForRecaptcha(): Promise<void> {
  return new Promise((resolve) => {
    if (isRecaptchaReady()) {
      resolve()
      return
    }

    const interval = window.setInterval(() => {
      if (isRecaptchaReady()) {
        window.clearInterval(interval)
        resolve()
      }
    }, 100)
  })
}

export function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === 'undefined' || !siteKey) {
    return Promise.resolve()
  }

  if (isRecaptchaReady()) {
    return Promise.resolve()
  }

  const existing = document.getElementById(RECAPTCHA_SCRIPT_ID)
  if (existing) {
    return waitForRecaptcha()
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = RECAPTCHA_SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
    script.async = true
    script.defer = true
    script.onload = () => {
      waitForRecaptcha().then(resolve).catch(reject)
    }
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'))
    document.head.appendChild(script)
  })
}

export async function getRecaptchaToken(siteKey: string, action = 'contact_form'): Promise<string> {
  if (!siteKey) {
    throw new Error('reCAPTCHA site key is not configured')
  }

  await loadRecaptchaScript(siteKey)

  return new Promise((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      window
        .grecaptcha!.execute(siteKey, { action })
        .then(resolve)
        .catch(reject)
    })
  })
}

export function getRecaptchaSiteKey(): string {
  return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
}
